'use client';

import React, { Fragment, useEffect } from 'react';
import { cx } from 'cva';
import Image from 'next/image';
import { useIsNonProfitAdmin } from '@/hooks/useUser';
import useUserProfile from '@/hooks/useUserProfile';
import Button from '@ui/Button';
import Typography from '@ui/Typography';
import useCelebrateDialogStore from 'components/Celebrate/celebrateDialog.store';
import PostType, { CampaignType } from 'constants/postType.constants';
import { hasAppAccess } from 'stores/userActions';
import { AppPreferenceItem } from 'utils/appPreference';
import { useCreatePostsStore } from '../../../utils/CreatePostDialogs';
import { useBreakpointHooks } from '@/utils/break-points.provider';
import OrgType from '@/constants/orgType.constant';

const userActions = [
  {
    appName: AppPreferenceItem.FundRaise,
    label: {
      verb: 'Campaign',
      noun: 'Fundraise',
    },
    icon: '/assets/images/screennav2.png',
    src: '/assets/icons/donations-made.svg',
    alt: 'fundraiser',
    checkPermission: true,
    dataId: 'create-a-fundraiser',
  },
  {
    appName: AppPreferenceItem.P2PFundRaise,
    label: {
      verb: 'Campaign',
      noun: 'Fundraise',
    },
    icon: '/assets/images/screennav2.png',
    src: '/assets/icons/create-fundraiser.svg',
    alt: 'p2p fundraiser',
    checkPermission: true,
    dataId: 'create-a-p2p-fundraiser',
  },
  {
    appName: AppPreferenceItem.InKindDonation,
    label: {
      verb: 'Campaign',
      noun: 'Fundraise',
    },
    icon: '/assets/images/screennav3.png',
    src: '/assets/icons/fundraise-campaign.svg',
    alt: 'in-kind fundraiser',
    checkPermission: true,
    dataId: 'create-an-in-kind-donation',
  },
  {
    appName: AppPreferenceItem.Volunteer,
    label: {
      verb: 'Create Volunteering Opportunity',
      noun: 'Volunteering',
    },
    icon: '/assets/images/screennav3.png',
    src: '/assets/icons/volunteer.svg',
    alt: 'voluteering',
    checkPermission: true,
    dataId: 'create-a-volunteering-opportunity',
  },
  {
    appName: AppPreferenceItem.Project,
    label: {
      verb: 'Create Project',
      noun: 'Project',
    },
    icon: '/assets/images/screennav3.png',
    src: '/assets/icons/bulb.svg',
    alt: 'project',
    checkPermission: false, // TODO: tomporarily ignoring the permission check for development
    dataId: 'create-a-project',
  },
  {
    appName: AppPreferenceItem.Celebration,
    label: {
      verb: 'Celebrate',
      noun: 'Celebrate',
    },
    icon: '/assets/images/screennav4.png',
    src: '/assets/icons/create-celebration.svg',
    alt: 'celebration',
    checkPermission: true,
    dataId: 'create-a-celebration',
  },
  {
    appName: AppPreferenceItem.CrowdFunding,
    label: {
      verb: 'CrowdFunding',
      noun: 'Fundraise',
    },
    icon: '/assets/icons/support.svg',
    src: '/assets/icons/support.svg',
    alt: 'crowd funding',
    checkPermission: true,
    dataId: 'create-a-crowd-funding',
  },
];

function CreatePostsMenu(props) {
  const {
    orientation = 'horizontal',
    onOptionSelect = () => {},
    createPostAction = null,
  } = props;
  const userProfile = useUserProfile();
  const isNonProfitAdmin = useIsNonProfitAdmin();

  const isExtraSmallScreen = useBreakpointHooks((bp) => bp.useSmaller('sm'));
  const isLargeScreen = useBreakpointHooks((bp) => bp.useGreater('lg'));
  const isLessThanMediumScreen = useBreakpointHooks((bp) =>
    bp.useSmaller('md'),
  );

  const [userApps, setUserApps] = React.useState([]);
  const setCelebrationDialogOpen = useCelebrateDialogStore(
    (state) => state.setOpen,
  );
  const { openDialog } = useCreatePostsStore();

  // if (
  //   createPostAction &&
  //   userActions.length < 3 &&
  //   !userActions.some((action) => action.appName === createPostAction.appName)
  // ) {
  //   userActions.unshift(createPostAction);
  // }

  useEffect(() => {
    if (userProfile.id > 0) {
      let apps = userActions.filter(
        (action) => !action.checkPermission || hasAppAccess(action.appName),
      );

      const appNames = new Set(apps.map((a) => a.appName));

      if (appNames.has(AppPreferenceItem.FundRaise)) {
        appNames.delete(AppPreferenceItem.InKindDonation);
        appNames.delete(AppPreferenceItem.P2PFundRaise);
        appNames.delete(AppPreferenceItem.CrowdFunding);
      }
      if (appNames.has(AppPreferenceItem.P2PFundRaise)) {
        appNames.delete(AppPreferenceItem.InKindDonation);
        appNames.delete(AppPreferenceItem.CrowdFunding);
      }
      if (appNames.has(AppPreferenceItem.InKindDonation)) {
        appNames.delete(AppPreferenceItem.CrowdFunding);
      }

      apps = apps.filter((a) => appNames.has(a.appName));

      // if (
      //   apps.some((a) => a.appName === AppPreferenceItem.FundRaise) &&
      //   apps.some((a) => a.appName === AppPreferenceItem.InKindDonation)
      // ) {
      //   apps = apps.filter(
      //     (a) => a.appName !== AppPreferenceItem.InKindDonation,
      //   );
      // }
      // if (
      //   apps.some((a) => a.appName === AppPreferenceItem.FundRaise) &&
      //   apps.some((a) => a.appName === AppPreferenceItem.P2PFundRaise)
      // ) {
      //   apps = apps.filter((a) => a.appName !== AppPreferenceItem.P2PFundRaise);
      // }

      // if (
      //   apps.some((a) => a.appName === AppPreferenceItem.P2PFundRaise) &&
      //   apps.some((a) => a.appName === AppPreferenceItem.InKindDonation)
      // ) {
      //   apps = apps.filter(
      //     (a) => a.appName !== AppPreferenceItem.InKindDonation,
      //   );
      // }

      const isNonProfitCommunity =
        userProfile?.activeCommunity?.orgType === OrgType.NonProfit;

      if (
        createPostAction &&
        apps.length > 0 &&
        !apps.some((action) => action.appName === createPostAction.appName)
      ) {
        if (orientation === 'vertical') {
          setUserApps([createPostAction, ...apps]);
        } else if (isNonProfitCommunity) {
          setUserApps(isNonProfitAdmin ? apps : [createPostAction, ...apps]);
        } else if (apps.length <= 4) {
          setUserApps([createPostAction, ...apps]);
        } else {
          setUserApps(apps);
        }
      } else {
        setUserApps(apps);
      }
    }
  }, [userProfile.id, createPostAction]);

  const handleClick = (action) => {
    onOptionSelect(action);
    switch (action) {
      case 'createPost':
        openDialog(PostType.UserPost);
        break;
      case AppPreferenceItem.InKindDonation:
        // openDialog(CampaignType.CollectGoods);
        openDialog(PostType.Campaign);
        break;
      case AppPreferenceItem.FundRaise:
      case AppPreferenceItem.P2PFundRaise:
        // openDialog(CampaignType.FundRaise);
        openDialog(PostType.Campaign);
        break;
      case AppPreferenceItem.Celebration:
        setCelebrationDialogOpen(true);
        break;
      case AppPreferenceItem.Volunteer:
        openDialog(PostType.Volunteering);
        break;
      case AppPreferenceItem.Project:
        openDialog(PostType.Project);
        break;
      case AppPreferenceItem.CrowdFunding:
        openDialog(PostType.CrowdFunding);
        break;
      default:
        break;
    }
  };

  const renderCreateTab = (action) => {
    switch (action.appName) {
      case AppPreferenceItem.Volunteer:
      case AppPreferenceItem.Project:
        return isNonProfitAdmin ? (
          <Button
            id={action.dataId}
            variant="plain"
            className="!lg:px-5"
            key={action.appName}
            onClick={() => handleClick(action.appName)}>
            <div
              className={cx(
                'flex items-center justify-center gap-2.5',
                isLessThanMediumScreen &&
                  orientation === 'horizontal' &&
                  'gap-2',
              )}>
              <Image
                width={25}
                height={25}
                src={action.src || ''}
                alt={action.alt}
              />
              <Typography
                variant="button"
                color="textSecondary"
                className="whitespace-nowrap">
                {action.label.noun}
              </Typography>
            </div>
          </Button>
        ) : null;
      case AppPreferenceItem.FundRaise:
      case AppPreferenceItem.InKindDonation:
      case AppPreferenceItem.LiveCampaigns:
      case AppPreferenceItem.Celebration:
      case AppPreferenceItem.CrowdFunding:
      default:
        return (
          <Button
            id={action.dataId}
            variant="plain"
            className="!lg:px-5"
            key={action.appName}
            onClick={() => handleClick(action.appName)}>
            <div
              className={cx(
                'flex items-center justify-center gap-2.5',
                isLessThanMediumScreen &&
                  orientation === 'horizontal' &&
                  'gap-2',
              )}>
              <Image
                width={25}
                height={25}
                src={action.src || ''}
                alt={action.alt}
              />
              <Typography
                variant="button"
                color="textSecondary"
                className="whitespace-nowrap">
                {action.label.noun}
              </Typography>
            </div>
          </Button>
        );
    }
  };

  return (
    <div>
      <div
        className={cx(
          'flex w-full',
          orientation === 'horizontal'
            ? 'flex-row items-center justify-evenly pt-4'
            : 'origin-top-left -translate-x-6 scale-125 transform flex-col items-start justify-start gap-4 overflow-hidden',
        )}>
        {(isExtraSmallScreen && orientation === 'horizontal'
          ? userApps.slice(0, 2)
          : userApps
        ).map((action) => (
          <Fragment key={action.appName}>{renderCreateTab(action)}</Fragment>
        ))}
      </div>
    </div>
  );
}

export default CreatePostsMenu;
